import React, { useState } from "react";
import { useDiaryContext } from "../../../../../hooks/useDiaryContext";
import { useNotificationsContext } from "../../../../../hooks/useNotificationsContext";
import Loading from "../../../../../utils/Loading";
import CheckIn from "./CheckIn/CheckIn";
import ManageFooter from "./Footer/ManageFooter";
import RunningLate from "./RunningLate/RunningLate";
import Stages from "./Stages/Stages";
import { CustomerRequest } from "./CustomerRequest/CustomerRequest";
import { Offers } from "./Offers/Offers";
import { CustomFields } from "./CustomFields/CustomFields";
import BookingActuals from "./BookingActuals/BookingActuals";
import SetUpRequirements from "./SetUpRequirements/SetUpRequirements";
import CustomFieldsEditable from "./CustomFields/CustomFieldsEditable";
import AdditionalDetails from "./AdditionalDetails/AdditionalDetails";
import { AlertBanner } from "../../../../blocks/alert-banner";
import { ViewBookingStatusEnum } from "collins-js-sdk";
import { updateBooking } from "./SelectedBookingManageController";
import { addOrUpdateBooking } from "../../../../../APIs/Booking/UpdateBooking";

const SelectedBookingManage = () => {
  const diaryContext = useDiaryContext();
  const { selectedBooking, bookings, setDiaryContext } = diaryContext;
  const notificationsContext = useNotificationsContext();
  const [isReopenLoading, setIsReopenLoading] = useState(false);

  if (!selectedBooking) {
    return <Loading />;
  }

  const handleUndo = async () => {
    if (!bookings) return;
    setIsReopenLoading(true);

    try {
      const updatedBookingForAPI = {
        ...selectedBooking,
        status: ViewBookingStatusEnum.InProgress
      };

      const response = await addOrUpdateBooking({
        booking: updatedBookingForAPI
      });

      if (response && !response.unsaved && response.id) {
        const { updatedBookings } = updateBooking({
          selectedBooking,
          bookings,
          bookingPropsToSave: { status: ViewBookingStatusEnum.InProgress }
        });
        setDiaryContext({ bookings: updatedBookings });
      } else {
        notificationsContext.addNotification({
          heading: "Failed to reopen booking",
          autoClose: 4000,
          toastType: "error"
        });
      }
    } catch {
      notificationsContext.addNotification({
        heading: "Failed to reopen booking",
        autoClose: 4000,
        toastType: "error"
      });
    } finally {
      setIsReopenLoading(false);
    }
  };

  const arrived = (selectedBooking.currentStage ?? "") !== "";

  return (
    <div className="ManageBooking flex flex-col flex-1 min-h-0">
      <div className="ManageBooking-content flex-1 min-h-0 overflow-y-auto">
        {selectedBooking.status === ViewBookingStatusEnum.Rejected && (
          <section className="pt-4 px-4">
            <AlertBanner
              variant="warning"
              title="Booking cancelled"
              description={
                <span>
                  This booking has been cancelled.{" "}
                  <button
                    onClick={handleUndo}
                    className="ManageBooking-undoButton underline cursor-pointer bg-transparent border-none p-0 font-[inherit] text-[inherit] disabled:opacity-50 disabled:no-underline disabled:pointer-events-none"
                    disabled={isReopenLoading}
                  >
                    Undo.
                  </button>
                </span>
              }
              dismissible={false}
            />
          </section>
        )}
        <CheckIn />
        <Stages />
        {!arrived && <RunningLate />}
        <CustomerRequest note={selectedBooking.notes} />
        <CustomFields showHeader={!selectedBooking.notes} />
        <Offers offer={selectedBooking.offer} />
        <section className="py-4 px-4 flex flex-col gap-3">
          <AdditionalDetails />
          <SetUpRequirements />
          <CustomFieldsEditable />
          <BookingActuals />
        </section>
      </div>
      <ManageFooter handleUndo={handleUndo} isReopenLoading={isReopenLoading} />
    </div>
  );
};

export default SelectedBookingManage;
